import { FormikErrors, useFormik } from "formik";
import { useEffect, useState } from "react";
import * as yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess } from "Components/constants/Common";
import { BaseSelect } from "Components/Base/BaseSelect";
import { listOfProductForDropdown } from "Api/common";
import { createIndent, updateIndent, viewIndent } from "Api/PurchaseOrder";
import {
  cancelLabel,
  submitLabel,
  updateLabel,
} from "Components/constants/Role";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import {
  AddIndentationLabel,
  AddProductLabel,
  AvailableQuantityLabel,
  EditIndentationLabel,
  ProductCodeLabel,
  ProductsKey,
  QuantityLabel,
} from "Components/constants/PurchaseOrder";

interface Product {
  productCode: string;
  availableQuantity: any;
  quantity: any;
}

interface ProductsInitialValues {
  products: Product[];
}

const validationSchema = yup.object().shape({
  products: yup.array().of(
    yup.object().shape({
      productCode: yup
        .string()
        .required(validationMessages.required(ProductCodeLabel)),
      quantity: yup
        .number()
        .min(1, validationMessages.positiveNumber(QuantityLabel))
        .required(validationMessages.required(QuantityLabel)),
    })
  ),
});

const AddIndentation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  document.title = id ? EditIndentationLabel : AddIndentationLabel;
  const [loader, setLoader] = useState(false);
  const [viewStaffData, setViewStaffData] = useState<any>([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [products, setProducts] = useState([]);

  const fetchProduct = () => {
    setLoader(true);
    let data;
    listOfProductForDropdown()
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          data = resp?.data;
          setProducts(
            data?.map((item: any) => ({
              value: item?.id,
              label: `${item?.product_code} (${item.batch_no})`,
              quantity: item?.quantity,
              id: item?.id,
            }))
          );
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoader(false);
      });
  };

  const initialValues: ProductsInitialValues = {
    products: [
      {
        productCode: "",
        availableQuantity: 0,
        quantity: null,
      },
    ],
  };
  const formik = useFormik<ProductsInitialValues>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        indentItem: values.products.map((item) => {
          return {
            product_id: item.productCode,
            quantity: Number(item.quantity),
          };
        }),
      };

      setBtnLoader(true);
      if (id) {
        updateIndent(Number(id), payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/indentation");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      } else {
        createIndent(payload)
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              toast.success(resp?.message);
              navigate("/indentation");
            } else {
              toast.error(resp?.message);
            }
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || err?.message);
            return err;
          })
          .finally(() => {
            setBtnLoader(false);
          });
      }
    },
  });

  useEffect(() => {
    if (viewStaffData) {
      const transformedProducts = viewStaffData?.indentItem?.map(
        (item: any) => {
          return {
            productCode: item.product_id,
            availableQuantity: item.product.quantity,
            quantity: item.quantity,
          };
        }
      );
      formik.setFieldValue(ProductsKey, transformedProducts);
    }
  }, [viewStaffData]);

  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorKey = Object.keys(formik.errors)[0];

      if (firstErrorKey) {
        const errorElement: HTMLElement | null = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}`
        );

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          if (
            errorElement.tagName === "INPUT" ||
            errorElement.tagName === "SELECT"
          ) {
            errorElement.focus();
          } else {
            const selectControl = errorElement.closest(".select__control");
            if (selectControl) {
              selectControl.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const selectInput = selectControl.querySelector("input, select");
              if (selectInput) {
                (selectInput as HTMLElement).focus();
              }
            }
          }
        }
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  const fetchViewRole = () => {
    if (id) {
      setLoader(true);
      viewIndent(Number(id))
        .then((response) => {
          if (checkStatusCodeSuccess(response.statusCode)) {
            const roleData = response.data;
            setViewStaffData(roleData);
          }
        })
        .catch((error) => {
          return error;
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    if (id) {
      fetchViewRole();
    }
    if (!id) {
      const newProduct = [
        {
          productCode: "",
          availableQuantity: 0,
          quantity: null,
        },
      ];
      formik.setFieldValue(ProductsKey, newProduct);
    }

    fetchProduct();
  }, []);

  useEffect(() => {
    Array.isArray(formik.values.products) &&
      formik.values.products.forEach((product: any, index: number) => {
        const result: any = products.find((item: any) => {
          return item.value === product.productCode;
        });

        if (result) {
          formik.setFieldValue(
            `products[${index}].availableQuantity`,
            result.quantity
          );
        }
      });
  }, [formik.values.products]);

  const addProductRow = () => {
    const newProduct = {
      productCode: "",
      availableQuantity: 0,
      quantity: null,
    };

    const currentProducts = formik.values.products || [];

    formik.setFieldValue(ProductsKey, [...currentProducts, newProduct]);
  };

  const removeProductRow = (index: number) => {
    const products = [...formik.values.products];
    products.splice(index, 1);
    formik.setFieldValue(ProductsKey, products);
  };

  const getFilteredProducts = (index: number) => {
    const selectedProductCodes = formik.values.products
      .map((product, idx) => (idx !== index ? product.productCode : null))
      .filter(Boolean);

    return products.filter(
      (product: any) => !selectedProductCodes.includes(product.value)
    );
  };

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4">
            {id ? EditIndentationLabel : AddIndentationLabel}
          </span>
          <button
            className="btn btn-danger"
            onClick={() => navigate("/indentation")}
          >
            <i className="mdi mdi-arrow-left" />
          </button>
        </div>
        <div className="card word-wrap">
          <div className="card-body">
            <form onSubmit={formik.handleSubmit} className="mt-2 mb-4">
              <div className="d-flex justify-content-end">
                <BaseButton
                  type="button"
                  label={AddProductLabel}
                  color="info"
                  onClick={() => addProductRow()}
                />
              </div>

              {Array.isArray(formik.values.products) &&
                formik.values.products.map(
                  (product: Product, index: number) => (
                    <div className="row mt-3" key={index}>
                      <div className="col-md-4 col-sm-6 col-12">
                        <BaseSelect
                          name={`products[${index}].productCode`}
                          label={ProductCodeLabel}
                          options={getFilteredProducts(index) || []}
                          value={formik.values.products[index].productCode}
                          placeholder={SelectPlaceHolder(ProductCodeLabel)}
                          handleChange={(field: any, value: any) => {
                            formik.setFieldValue(field, value);
                          }}
                          handleBlur={formik.handleBlur}
                          error={
                            formik.touched.products?.[index]?.productCode &&
                            (
                              formik.errors.products?.[
                                index
                              ] as FormikErrors<Product>
                            )?.productCode
                              ? (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                ).productCode
                              : ""
                          }
                          touched={
                            formik.touched.products?.[index]?.productCode ||
                            false
                          }
                          required
                        />
                      </div>

                      <div className="col-md-4 col-sm-6 col-12">
                        <BaseInput
                          name={`products[${index}].availableQuantity`}
                          label={AvailableQuantityLabel}
                          value={
                            formik.values.products[index].availableQuantity
                          }
                          onChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          disabled
                          className="text-muted"
                          error={
                            formik.touched.products?.[index]
                              ?.availableQuantity &&
                            (
                              formik.errors.products?.[
                                index
                              ] as FormikErrors<Product>
                            )?.availableQuantity
                              ? (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                ).availableQuantity
                              : ""
                          }
                          touched={
                            formik.touched.products?.[index]
                              ?.availableQuantity || false
                          }
                        />
                      </div>

                      <div className="col-md-3 col-sm-6 col-12">
                        <BaseInput
                          name={`products[${index}].quantity`}
                          label={QuantityLabel}
                          type="number"
                          placeholder="0"
                          value={formik.values.products[index].quantity}
                          onChange={formik.handleChange}
                          handleBlur={formik.handleBlur}
                          error={
                            formik.touched.products?.[index]?.quantity &&
                            (
                              formik.errors.products?.[
                                index
                              ] as FormikErrors<Product>
                            )?.quantity
                              ? (
                                  formik.errors.products?.[
                                    index
                                  ] as FormikErrors<Product>
                                ).quantity
                              : ""
                          }
                          touched={
                            formik.touched.products?.[index]?.quantity || false
                          }
                          required
                        />
                      </div>

                      <div className="col-md-1 col-sm-6 col-12 d-flex justify-content-end mr-4 align-items-end">
                        {index > 0 && (
                          <i
                            className="mdi mdi-delete-outline fs-3 px-3 text-danger"
                            onClick={() => removeProductRow(index)}
                            style={{ cursor: "pointer" }}
                          ></i>
                        )}
                      </div>
                    </div>
                  )
                )}

              <div className="row mt-3 d-flex justify-content-end">
                <div className="col-auto d-flex justify-content-end gap-2 text-end">
                  <BaseButton
                    type="submit"
                    label={id ? updateLabel : submitLabel}
                    color="success"
                    loader={btnLoader}
                    disable={btnLoader}
                  />
                  <BaseButton
                    type="button"
                    label={cancelLabel}
                    onClick={() => navigate("/indentation")}
                    color="danger"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddIndentation;
